import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Upload,
  Trash,
  X,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Video as VideoIcon,
  Loader2,
  Copy,
  Download,
  Link as LinkIcon,
  RefreshCcw,
  CheckSquare,
  Square,
  Search,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuthStore } from "../../Store/auth";
import { API_ORIGIN } from "../../config/api";

const API = API_ORIGIN;
const MAX_IMAGE_MB = 15;
const MAX_VIDEO_MB = 200;
const ACCEPT = "image/*,video/*";

export default function CoachGallery() {
  const { token, coachId, logout } = useAuthStore();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters / search / selection
  const [filterType, setFilterType] = useState("all"); // all | image | video
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  // upload queue (multiple)
  const [queue, setQueue] = useState([]); // {id, file, type, progress, status:'idle'|'uploading'|'done'|'error', controller, errorMessage}
  const fileInputRef = useRef(null);

  // lightbox
  const [isOpen, setIsOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  // ---------- Load ----------
  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/GalleryMedia/coach/${coachId}`, authHeaders);
      const items = Array.isArray(res.data) ? res.data : [];
      // sort newest first if createdAt exists, else by id desc
      items.sort((a, b) => {
        const A = a.createdAt ? new Date(a.createdAt).getTime() : (a.id || 0);
        const B = b.createdAt ? new Date(b.createdAt).getTime() : (b.id || 0);
        return B - A;
      });
      setGallery(items);
    } catch (err) {
      if (err?.response?.status === 401 && logout) logout();
      console.error("Error fetching gallery:", err);
      toast.error("Failed to load gallery.");
    } finally {
      setLoading(false);
    }
  }, [coachId, authHeaders, logout]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  // ---------- Filtering ----------
  const filtered = useMemo(() => {
    let list = gallery;
    if (filterType !== "all") list = list.filter((i) => i.mediaType === filterType);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (i) =>
          (i.caption || "").toLowerCase().includes(q) ||
          (i.url || "").toLowerCase().includes(q) ||
          String(i.id || "").includes(q)
      );
    }
    return list;
  }, [gallery, filterType, query]);

  // ---------- Selection ----------
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} item(s)?`)) return;
    try {
      await Promise.all(
        [...selected].map((id) => axios.delete(`${API}/api/GalleryMedia/${id}`, authHeaders))
      );
      setGallery((g) => g.filter((x) => !selected.has(x.id)));
      clearSelection();
      toast.success("Selected media deleted.");
    } catch (err) {
      if (err?.response?.status === 401 && logout) logout();
      console.error("Bulk delete failed:", err);
      toast.error("Failed to delete some items.");
    }
  };

  // ---------- Upload ----------
  const validateFile = (f) => {
    const isVideo = f.type.startsWith("video");
    const isImage = f.type.startsWith("image");
    if (!isVideo && !isImage) return "Unsupported file type.";
    const sizeMB = f.size / (1024 * 1024);
    if (isImage && sizeMB > MAX_IMAGE_MB) return `Image too large (>${MAX_IMAGE_MB}MB).`;
    if (isVideo && sizeMB > MAX_VIDEO_MB) return `Video too large (>${MAX_VIDEO_MB}MB).`;
    return null;
  };

  const enqueueFiles = (files) => {
    const added = [];
    for (const f of files) {
      const error = validateFile(f);
      if (error) {
        toast.warn(`${f.name}: ${error}`);
        continue;
      }
      added.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file: f,
        type: f.type.startsWith("video") ? "video" : "image",
        progress: 0,
        status: "idle",
        controller: null,
        errorMessage: "",
      });
    }
    if (added.length) setQueue((q) => [...q, ...added]);
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    enqueueFiles(files);
    // reset input to allow re-pick same file
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    enqueueFiles(files);
  };

  const onDragOver = (e) => e.preventDefault();

  const startUploadItem = async (itemId) => {
    setQueue((q) =>
      q.map((it) => (it.id === itemId ? { ...it, status: "uploading", progress: 0, errorMessage: "" } : it))
    );

    const item = queue.find((x) => x.id === itemId);
    if (!item) return;

    const formData = new FormData();
    formData.append("File", item.file);
    formData.append("MediaType", item.type);
    formData.append("CoachId", coachId);

    const controller = new AbortController();
    setQueue((q) => q.map((it) => (it.id === itemId ? { ...it, controller } : it)));

    try {
      await axios.post(`${API}/api/GalleryMedia/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        onUploadProgress: (p) => {
          const pct = p.total ? Math.round((p.loaded / p.total) * 100) : 0;
          setQueue((q) => q.map((it) => (it.id === itemId ? { ...it, progress: pct } : it)));
        },
      });

      setQueue((q) => q.map((it) => (it.id === itemId ? { ...it, status: "done", progress: 100 } : it)));
      toast.success(`${item.file.name} uploaded.`);
      // refresh just once after batch using a microtask
      Promise.resolve().then(() => fetchGallery());
    } catch (err) {
      if (axios.isCancel(err)) {
        setQueue((q) => q.map((it) => (it.id === itemId ? { ...it, status: "idle", progress: 0 } : it)));
        toast.info("Upload cancelled.");
      } else {
        if (err?.response?.status === 401 && logout) logout();
        console.error("Upload failed:", err);
        setQueue((q) =>
          q.map((it) =>
            it.id === itemId ? { ...it, status: "error", errorMessage: err?.response?.data?.message || "Failed to upload." } : it
          )
        );
        toast.error(`${item.file.name}: upload failed.`);
      }
    }
  };

  const startUploadAll = async () => {
    const ids = queue.filter((it) => it.status === "idle").map((it) => it.id);
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await startUploadItem(id);
    }
  };

  const cancelUpload = (id) => {
    const it = queue.find((x) => x.id === id);
    it?.controller?.abort();
  };

  const removeFromQueue = (id) => setQueue((q) => q.filter((it) => it.id !== id));

  // ---------- Delete ----------
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this media?")) return;
    try {
      await axios.delete(`${API}/api/GalleryMedia/${id}`, authHeaders);
      setGallery((g) => g.filter((x) => x.id !== id));
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      toast.success("Media deleted.");
    } catch (error) {
      if (error?.response?.status === 401 && logout) logout();
      console.error("Delete failed:", error);
      toast.error("Failed to delete media.");
    }
  };

  // ---------- Lightbox ----------
  const openLightbox = (idx) => {
    setCurrent(idx);
    setIsOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setIsOpen(false);
    document.body.style.overflow = "";
  };

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % filtered.length);
  }, [filtered.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, prev, next]);

  const onTouchStart = (e) => (touchStartX.current = e.changedTouches[0].clientX);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touchStartX.current ?? 0);
    if (dx > 40) prev();
    if (dx < -40) next();
    touchStartX.current = null;
  };

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  // ---------- UI ----------
  return (
    <div className="p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-extrabold">My Gallery</h1>
          <p className="text-xs text-gray-500">
            Drag & drop multiple files. Images ≤ {MAX_IMAGE_MB}MB, videos ≤ {MAX_VIDEO_MB}MB.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search caption / URL / ID"
              className="pl-9 pr-3 py-2 border rounded-lg w-full sm:w-64"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => setFilterType("all")}
              className={`border ${filterType === "all" ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}
            >
              All
            </Button>
            <Button
              type="button"
              onClick={() => setFilterType("image")}
              className={`border ${filterType === "image" ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}
            >
              <ImageIcon className="w-4 h-4 mr-1" /> Images
            </Button>
            <Button
              type="button"
              onClick={() => setFilterType("video")}
              className={`border ${filterType === "video" ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}
            >
              <VideoIcon className="w-4 h-4 mr-1" /> Videos
            </Button>
          </div>
          <Button type="button" variant="outline" onClick={fetchGallery} className="flex items-center gap-2">
            <RefreshCcw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Dropzone / Picker */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className="mb-6 rounded-2xl border-2 border-dashed border-gray-300 p-4 sm:p-6 bg-white/70"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onPickFiles}
            className="w-full sm:w-auto"
            accept={ACCEPT}
          />
          <div className="text-sm text-gray-600">or drag & drop files here</div>
          {queue.some((q) => q.status === "idle") && (
            <Button type="button" onClick={startUploadAll} className="ml-auto">
              <Upload className="w-4 h-4 mr-2" /> Upload all
            </Button>
          )}
        </div>

        {/* Upload queue */}
        {queue.length > 0 && (
          <div className="mt-4 space-y-2">
            {queue.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between gap-3 border rounded-lg p-2 bg-white"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center shrink-0">
                    {it.type === "image" ? <ImageIcon className="w-5 h-5" /> : <VideoIcon className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{it.file.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {(it.file.size / (1024 * 1024)).toFixed(1)} MB • {it.type}
                    </div>
                    {it.status !== "idle" && (
                      <div className="mt-1 h-1.5 w-40 bg-gray-200 rounded overflow-hidden">
                        <div
                          className={`h-full ${it.status === "error" ? "bg-red-500" : "bg-blue-600"}`}
                          style={{ width: `${it.progress || 0}%` }}
                        />
                      </div>
                    )}
                    {it.status === "error" && (
                      <div className="text-xs text-red-600 mt-1">{it.errorMessage}</div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {it.status === "idle" && (
                    <Button size="sm" onClick={() => startUploadItem(it.id)} className="flex items-center gap-1">
                      <Upload className="w-4 h-4" /> Upload
                    </Button>
                  )}
                  {it.status === "uploading" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => cancelUpload(it.id)}
                      className="flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Cancel
                    </Button>
                  )}
                  {(it.status === "done" || it.status === "error") && (
                    <Button size="sm" variant="outline" onClick={() => removeFromQueue(it.id)}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions bar for selections */}
      {selected.size > 0 && (
        <div className="mb-4 p-3 border rounded-xl bg-white flex items-center justify-between">
          <div className="text-sm">
            <strong>{selected.size}</strong> selected
          </div>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={bulkDelete} className="flex items-center gap-2">
              <Trash className="w-4 h-4" /> Delete selected
            </Button>
            <Button variant="outline" onClick={clearSelection}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Gallery grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? (
          [...Array(8)].map((_, i) => <div key={i} className="h-56 bg-gray-200 animate-pulse rounded-xl" />)
        ) : filtered.length === 0 ? (
          <p className="text-gray-500 col-span-full text-center">No media found.</p>
        ) : (
          filtered.map((item, idx) => {
            const isSelected = selected.has(item.id);
            return (
              <Card key={item.id ?? idx} className="relative group overflow-hidden">
                {/* select toggle */}
                <button
                  onClick={() => toggleSelect(item.id)}
                  className="absolute top-2 left-2 z-10 p-1.5 rounded bg-white/90 hover:bg-white shadow"
                  aria-label={isSelected ? "Unselect" : "Select"}
                >
                  {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>

                <button
                  type="button"
                  onClick={() => openLightbox(idx)}
                  className="block w-full h-56 focus:outline-none rounded-md overflow-hidden"
                  aria-label={`Open ${item.mediaType} ${idx + 1}`}
                >
                  {item.mediaType === "image" ? (
                    <img
                      src={item.url}
                      alt={item.caption || `media ${idx + 1}`}
                      className="w-full h-56 object-cover rounded-md"
                      loading="lazy"
                    />
                  ) : (
                    <video
                      src={item.url}
                      className="w-full h-56 object-cover rounded-md pointer-events-none"
                      playsInline
                      muted
                    />
                  )}
                </button>

                <CardContent className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition">
                  <div className="flex gap-2">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => copyLink(item.url)}
                      className="rounded-full w-10 h-10"
                      title="Copy link"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </Button>
                    <a
                      href={item.url}
                      download
                      className="rounded-full w-10 h-10 grid place-items-center border bg-white hover:bg-gray-50"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleDelete(item.id)}
                      className="rounded-full w-10 h-10"
                      title="Delete"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Lightbox */}
      {isOpen && filtered[current] && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-2 md:p-6"
          role="dialog"
          aria-modal="true"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-3 right-3 md:top-6 md:right-6 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Prev */}
          {filtered.length > 1 && (
            <button
              onClick={prev}
              className="absolute left-2 md:left-6 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
              aria-label="Previous"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}

          {/* Media */}
          <div className="w-full max-w-5xl">
            {filtered[current].mediaType === "video" ? (
              <video
                key={filtered[current].url}
                src={filtered[current].url}
                controls
                autoPlay
                className="w-full max-h-[80vh] object-contain rounded-lg shadow-xl bg-black"
              />
            ) : (
              <img
                key={filtered[current].url}
                src={filtered[current].url}
                alt={`Gallery item ${current + 1}`}
                className="w-full max-h-[80vh] object-contain rounded-lg shadow-xl"
              />
            )}
            <div className="mt-3 text-center text-white/80 text-sm">
              {current + 1} / {filtered.length}
            </div>
            <div className="mt-2 flex items-center justify-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyLink(filtered[current].url)}
                className="bg-white/10 text-white border-white/30 hover:bg-white/20"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy link
              </Button>
              <a
                href={filtered[current].url}
                download
                className="px-3 py-2 rounded-lg bg-white/10 text-white border border-white/30 hover:bg-white/20 text-sm flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download
              </a>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(filtered[current].id)}
              >
                <Trash className="w-4 h-4 mr-1" /> Delete
              </Button>
            </div>
          </div>

          {/* Next */}
          {filtered.length > 1 && (
            <button
              onClick={next}
              className="absolute right-2 md:right-6 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
              aria-label="Next"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          )}

          {/* backdrop catcher */}
          <button aria-hidden="true" className="absolute inset-0 -z-10" onClick={closeLightbox} />
        </div>
      )}
    </div>
  );
}
